import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiService, downloadBlob } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  ArrowLeft,
  Edit,
  Plus,
  Download,
  FileText,
  Loader2,
  GraduationCap,
  User,
  School,
  Calendar,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  Trash2,
  CheckCircle,
  Clock,
  AlertTriangle,
  Activity,
  Sparkles,
  Copy,
  Check,
  Printer,
  Save,
  StickyNote,
  X,
  UserCircle,
  Hash
} from 'lucide-react';
import AddInterventionModal from '../components/AddInterventionModal';
import AddProgressModal from '../components/AddProgressModal';
import EditStudentModal from '../components/EditStudentModal';
import EditInterventionModal from '../components/EditInterventionModal';
import PrintGraphModal from '../components/PrintGraphModal';
import AddNoteModal from '../components/AddNoteModal';
import EditNoteModal from '../components/EditNoteModal';
import StudentContextProfile from '../components/StudentContextProfile';
import { normalizeSupportAreaArray } from '../constants/supportAreas';

export default function StudentProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, user } = useAuth();
  const [student, setStudent] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal states
  const [showEditStudent, setShowEditStudent] = useState(false);
  const [showAddIntervention, setShowAddIntervention] = useState(false);
  const [showAddProgress, setShowAddProgress] = useState(false);
  const [selectedInterventionForProgress, setSelectedInterventionForProgress] = useState(null);
  const [showEditIntervention, setShowEditIntervention] = useState(false);
  const [selectedInterventionForEdit, setSelectedInterventionForEdit] = useState(null);
  
  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState(false);
  
  // AI Summary state
  const [aiSummary, setAiSummary] = useState('');
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryCopied, setAiSummaryCopied] = useState(false);
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showEditNote, setShowEditNote] = useState(false);
  const [selectedNoteForEdit, setSelectedNoteForEdit] = useState(null);
  const [savingAISummaryToNotes, setSavingAISummaryToNotes] = useState(false);
  
  // Progress tab filter state
  const [selectedInterventionFilter, setSelectedInterventionFilter] = useState('all');
  const [graphMode, setGraphMode] = useState('single'); // 'single' or 'all'

  // Reset filter when switching to "All Interventions" mode
  const handleGraphModeChange = (mode) => {
    setGraphMode(mode);
    if (mode === 'all') {
      // Reset the intervention filter so the table shows all data
      setSelectedInterventionFilter('all');
    }
  };

  useEffect(() => {
    fetchStudentData();
  }, [id]);

  // Handler to open Add Progress modal from intervention row
  const handleAddProgressFromIntervention = (interventionId) => {
    setSelectedInterventionForProgress(interventionId);
    setShowAddProgress(true);
  };

  // Handler to navigate from intervention row to Progress tab with that intervention selected
  const handleNavigateToInterventionProgress = (interventionId) => {
    setSelectedInterventionFilter(interventionId);
    setGraphMode('single');
    setActiveTab('progress');
  };

  // Handler to open Edit Intervention modal
  const handleEditIntervention = (intervention) => {
    setSelectedInterventionForEdit(intervention);
    setShowEditIntervention(true);
  };

  // Handler to open Add Progress modal from top button
  const handleAddProgressGeneral = () => {
    setSelectedInterventionForProgress(null);
    setShowAddProgress(true);
  };

  const fetchStudentData = async () => {
    setLoading(true);
    try {
      const [studentRes, interventionsRes, progressRes, notesRes] = await Promise.all([
        apiService.getStudent(id),
        apiService.getInterventions({ student_id: id }),
        apiService.getProgress({ student_id: id }),
        apiService.getStudentNotes(id)
      ]);

      setStudent(studentRes.data);
      setInterventions(interventionsRes.data);
      setProgress(progressRes.data);
      setNotes(notesRes.data);
    } catch (error) {
      console.error('Failed to fetch student data:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load student data');
        navigate('/students');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportStudent = async () => {
    try {
      const response = await apiService.exportStudentFull(id);
      const safeName = student.full_name.replace(/\s+/g, '_');
      const date = new Date().toISOString().split('T')[0];
      downloadBlob(response.data, `mtss_student_${safeName}_${date}.csv`);
      toast.success('Student data exported');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export student data');
    }
  };

  const handleDeleteIntervention = async (interventionId) => {
    if (!window.confirm('Are you sure you want to delete this intervention?')) return;
    
    try {
      await apiService.deleteIntervention(interventionId);
      setInterventions(interventions.filter(i => i.id !== interventionId));
      toast.success('Intervention deleted');
    } catch (error) {
      toast.error('Failed to delete intervention');
    }
  };

  const handleMarkCompleted = async (interventionId) => {
    try {
      await apiService.updateIntervention(interventionId, {
        active_status: 'Completed',
        end_date: new Date().toISOString().split('T')[0]
      });
      fetchStudentData();
      toast.success('Intervention marked as completed');
    } catch (error) {
      toast.error('Failed to update intervention');
    }
  };

  const handleDeleteProgress = async (progressId) => {
    if (!window.confirm('Are you sure you want to delete this data point?')) return;
    
    try {
      await apiService.deleteProgress(progressId);
      setProgress(progress.filter(p => p.id !== progressId));
      toast.success('Progress data deleted');
    } catch (error) {
      toast.error('Failed to delete progress data');
    }
  };

  // AI Summary generation
  const handleGenerateAISummary = async () => {
    setAiSummaryLoading(true);
    setAiSummary('');
    try {
      const response = await apiService.generateAISummary(id);
      if (response.success) {
        setAiSummary(response.summary);
        toast.success('Summary drafted successfully');
      } else {
        toast.error('Failed to generate summary');
      }
    } catch (error) {
      console.error('AI Summary error:', error);
      toast.error('Failed to generate summary. Please try again.');
    } finally {
      setAiSummaryLoading(false);
    }
  };

  const handleCopyAISummary = async () => {
    try {
      await navigator.clipboard.writeText(aiSummary);
      setAiSummaryCopied(true);
      toast.success('Summary copied to clipboard');
      setTimeout(() => setAiSummaryCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  // Save AI Summary to Notes
  const handleSaveAISummaryToNotes = async () => {
    if (!aiSummary.trim()) return;
    
    setSavingAISummaryToNotes(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await apiService.createStudentNote({
        student_id: id,
        note_date: today,
        note_text: aiSummary.trim()
      });
      toast.success('AI summary saved to Notes');
      // Refresh notes
      const notesRes = await apiService.getStudentNotes(id);
      setNotes(notesRes.data);
    } catch (error) {
      console.error('Failed to save AI summary to notes:', error);
      toast.error('Failed to save to notes');
    } finally {
      setSavingAISummaryToNotes(false);
    }
  };

  // Delete note handler
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await apiService.deleteStudentNote(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  // Edit note handler
  const handleEditNote = (note) => {
    setSelectedNoteForEdit(note);
    setShowEditNote(true);
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-4 w-4" />;
      case 'declining': return <TrendingDown className="h-4 w-4" />;
      case 'flat': return <Minus className="h-4 w-4" />;
      default: return null;
    }
  };

  const getTrendClass = (trend) => {
    switch (trend) {
      case 'improving': return 'trend-up';
      case 'declining': return 'trend-down';
      case 'flat': return 'trend-flat';
      default: return '';
    }
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case 'Tier 1': return 'badge-tier1';
      case 'Tier 2': return 'badge-tier2';
      case 'Tier 3': return 'badge-tier3';
      default: return '';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Improving': return 'badge-improving';
      case 'Limited Progress': return 'badge-limited';
      case 'Needs Review': return 'badge-review';
      default: return '';
    }
  };

  const getInterventionStatusClass = (status) => {
    switch (status) {
      case 'Active': return 'badge-active';
      case 'Completed': return 'badge-completed';
      case 'Discontinued': return 'badge-discontinued';
      default: return '';
    }
  };

  const getReviewStatusBadge = () => {
    if (!student?.review_status) return null;
    
    switch (student.review_status) {
      case 'overdue':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <AlertTriangle className="h-3 w-3" />
            Review Overdue
          </span>
        );
      case 'review_soon':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <Clock className="h-3 w-3" />
            Review Soon
          </span>
        );
      case 'upcoming':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <Calendar className="h-3 w-3" />
            Upcoming Review
          </span>
        );
      default:
        return null;
    }
  };

  // Get the selected intervention object for chart reference lines
  const selectedIntervention = selectedInterventionFilter !== 'all' 
    ? interventions.find(i => i.id === selectedInterventionFilter) 
    : null;

  // Prepare chart data - filter by selected intervention (Single mode)
  const filteredProgressForChart = selectedInterventionFilter === 'all'
    ? [] // Don't show combined data - require an intervention selection
    : progress.filter(p => p.intervention_id === selectedInterventionFilter);

  const chartData = [...filteredProgressForChart]
    .sort((a, b) => new Date(a.date_collected) - new Date(b.date_collected))
    .map(p => ({
      date: p.date_collected,
      score: p.score,
      measure: p.measure_name
    }));

  // Prepare multi-line chart data for "All Interventions" graph mode
  const interventionsWithProgress = interventions.filter(intervention => 
    progress.some(p => p.intervention_id === intervention.id)
  );

  // Get unique score units across all progress data for interventions
  const scoreUnitsMap = {};
  interventionsWithProgress.forEach(intervention => {
    const interventionProgress = progress.filter(p => p.intervention_id === intervention.id);
    const units = [...new Set(interventionProgress.map(p => p.score_unit).filter(Boolean))];
    scoreUnitsMap[intervention.id] = units[0] || 'score';
  });
  const uniqueUnits = [...new Set(Object.values(scoreUnitsMap))];
  const hasMultipleUnits = uniqueUnits.length > 1;

  // Build multi-line chart data: each date gets a row with intervention scores
  const allDates = [...new Set(progress
    .filter(p => interventionsWithProgress.some(i => i.id === p.intervention_id))
    .map(p => p.date_collected)
  )].sort((a, b) => new Date(a) - new Date(b));

  const multiLineChartData = allDates.map(date => {
    const row = { date };
    interventionsWithProgress.forEach(intervention => {
      const dataPoint = progress.find(
        p => p.date_collected === date && p.intervention_id === intervention.id
      );
      if (dataPoint) {
        row[intervention.id] = dataPoint.score;
      }
    });
    return row;
  });

  // Color palette for multi-line chart - FrameEd colors
  const lineColors = [
    '#2C4C3B', // dark green (primary)
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#06b6d4', // cyan
    '#ec4899', // pink
    '#84cc16', // lime
  ];

  // Progress table data - filter based on graphMode and selectedInterventionFilter
  // In "All Interventions" mode, always show all progress data
  // In "Single Intervention" mode, filter by selected intervention (or show all if none selected)
  const filteredProgressForTable = graphMode === 'all'
    ? progress
    : (selectedInterventionFilter === 'all'
        ? progress
        : progress.filter(p => p.intervention_id === selectedInterventionFilter));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="page-container">
        <div className="empty-state" data-testid="student-access-denied">
          <h3 className="empty-title">Not authorized</h3>
          <p className="empty-text">
            You don't have access to this student. You can only view students assigned to you as a classroom teacher or interventionist.
          </p>
          <Button
            className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
            onClick={() => navigate('/students')}
            data-testid="access-denied-back-btn"
          >
            Back to Students
          </Button>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="page-container">
        <p>Student not found</p>
      </div>
    );
  }

  const latestScore = progress.length > 0 ? progress[0].score : null;
  const latestDate = progress.length > 0 ? progress[0].date_collected : null;
  const daysInSupport = student.support_start_date
    ? Math.floor((new Date() - new Date(student.support_start_date)) / (1000 * 60 * 60 * 24))
    : null;
  const activeInterventions = interventions.filter(i => i.active_status === 'Active');
  const concernStr = Array.isArray(student.concern_area) 
    ? normalizeSupportAreaArray(student.concern_area).join(', ') 
    : student.concern_area;

  // Map target skills to broader concern areas (for Overview tab display)
  const mapTargetSkillToConcernArea = (targetSkill) => {
    if (!targetSkill) return null;
    const skill = targetSkill.toLowerCase();
    
    // Reading-related
    if (skill.includes('reading') || skill.includes('fluency') || skill.includes('decoding') || 
        skill.includes('phonics') || skill.includes('phonemic') || skill.includes('comprehension') ||
        skill.includes('vocabulary') || skill.includes('sight word') || skill.includes('orf')) {
      return 'Reading';
    }
    // Writing-related
    if (skill.includes('writing') || skill.includes('spelling') || skill.includes('handwriting') ||
        skill.includes('composition') || skill.includes('grammar') || skill.includes('sentence')) {
      return 'Writing';
    }
    // Math-related
    if (skill.includes('math') || skill.includes('number') || skill.includes('calculation') ||
        skill.includes('computation') || skill.includes('multiplication') || skill.includes('addition') ||
        skill.includes('subtraction') || skill.includes('division') || skill.includes('fraction') ||
        skill.includes('algebra') || skill.includes('geometry') || skill.includes('problem solving')) {
      return 'Math';
    }
    // Behavior-related
    if (skill.includes('behavior') || skill.includes('self-regulation') || skill.includes('attention') ||
        skill.includes('focus') || skill.includes('impulse') || skill.includes('emotional') ||
        skill.includes('coping') || skill.includes('anger') || skill.includes('compliance')) {
      return 'Behavior';
    }
    // Social Skills-related
    if (skill.includes('social') || skill.includes('peer') || skill.includes('friendship') ||
        skill.includes('cooperation') || skill.includes('communication') || skill.includes('interaction')) {
      return 'Social Skills';
    }
    // Speech/Language-related
    if (skill.includes('speech') || skill.includes('language') || skill.includes('articulation') ||
        skill.includes('expressive') || skill.includes('receptive') || skill.includes('verbal')) {
      return 'Speech/Language';
    }
    // Attendance-related
    if (skill.includes('attendance') || skill.includes('tardy') || skill.includes('absent') ||
        skill.includes('truancy') || skill.includes('engagement')) {
      return 'Attendance';
    }
    return 'Other';
  };

  // Derive concern areas from active interventions (for Overview tab only)
  const derivedConcernAreas = activeInterventions.length > 0
    ? [...new Set(activeInterventions
        .map(i => mapTargetSkillToConcernArea(i.target_skill))
        .filter(Boolean)
      )]
    : [];
  
  // Use derived areas if available, otherwise fall back to stored concern area
  const displayConcernArea = derivedConcernAreas.length > 0 
    ? derivedConcernAreas.join(', ')
    : concernStr || '—';

  return (
    <div className="page-container student-profile-page" data-testid="student-profile-page">
      {/* Back Button */}
      <div className="mb-6 no-print">
        <Link to="/students" className="inline-flex items-center text-[#595959] hover:text-[#1C1C1C] text-sm font-medium" data-testid="back-to-students-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Students
        </Link>
      </div>

      {/* Profile Header */}
      <div className="profile-header" data-testid="profile-header">
        <div className="profile-top">
          <div className="profile-avatar">
            {student.full_name?.charAt(0) || 'S'}
          </div>
          <div className="profile-info flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="profile-name" data-testid="student-name">{student.full_name}</h1>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowEditStudent(true)} 
                data-testid="edit-student-btn" 
                className="text-[#595959] hover:text-[#2C4C3B] hover:bg-[#2C4C3B]/10 -ml-1"
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Summary Sentence - based on interventions */}
            <p className="text-[#595959] text-sm mt-1 mb-2" data-testid="student-summary">
              <strong>{student.first_name}</strong> is currently receiving <strong>{student.current_tier}</strong> support
              {activeInterventions.length > 0 ? (
                <> focusing on <strong>{[...new Set(activeInterventions.map(i => i.target_skill))].join(', ')}</strong>. 
                Currently has <strong>{activeInterventions.length}</strong> active intervention{activeInterventions.length > 1 ? 's' : ''}.</>
              ) : (
                <>. No active interventions.</>
              )}
            </p>
            
            <div className="profile-meta">
              <span className="profile-meta-item">
                <GraduationCap className="h-4 w-4" />
                Grade {student.grade}
              </span>
              {student.student_id_number && (
                <span className="profile-meta-item">
                  <Hash className="h-4 w-4" />
                  ID: {student.student_id_number}
                </span>
              )}
              <span className="profile-meta-item">
                <User className="h-4 w-4" />
                {student.teacher_name}
              </span>
              <span className="profile-meta-item">
                <School className="h-4 w-4" />
                {student.school_name}
              </span>
              {student.review_date && (
                <span className="profile-meta-item">
                  <Calendar className="h-4 w-4" />
                  Review: {student.review_date}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={`badge ${getTierBadgeClass(student.current_tier)}`} data-testid="student-tier">
                {student.current_tier}
              </span>
              <span className={`badge ${getStatusBadgeClass(student.status)}`} data-testid="student-status">
                {student.status}
              </span>
              {getReviewStatusBadge()}
            </div>
          </div>
          <div className="profile-actions">
            <Button 
              className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl font-medium" 
              onClick={() => setShowAddIntervention(true)} 
              data-testid="add-intervention-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Intervention
            </Button>
            <Button 
              className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl font-medium" 
              onClick={handleAddProgressGeneral} 
              data-testid="add-progress-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Data
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExportStudent} 
              data-testid="export-student-btn" 
              className="rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B]"
            >
              <Download className="h-4 w-4 mr-2" />
              Export to CSV
            </Button>
            <Link to={`/reports?student=${id}`}>
              <Button variant="outline" className="rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B]" data-testid="generate-report-btn">
                <FileText className="h-4 w-4 mr-2" />
                Report
              </Button>
            </Link>
          </div>
        </div>

        <div className="profile-stats">
          <div className="profile-stat">
            <div className="profile-stat-value">{student.baseline_score || '—'}</div>
            <div className="profile-stat-label">Baseline</div>
          </div>
          <div className="profile-stat">
            <div className="profile-stat-value text-[#2C4C3B]">{latestScore || '—'}</div>
            <div className="profile-stat-label">
              Latest Score
              {latestDate && <span className="text-xs text-[#A3A3A3] block">{latestDate}</span>}
            </div>
          </div>
          <div className="profile-stat">
            <div className="profile-stat-value">{student.goal_score || '—'}</div>
            <div className="profile-stat-label">Goal</div>
          </div>
          <div className="profile-stat">
            <div className={`profile-stat-value trend-indicator ${getTrendClass(student.trend)}`}>
              {getTrendIcon(student.trend)}
              <span className="ml-1 capitalize">{student.trend || 'insufficient'}</span>
            </div>
            <div className="profile-stat-label">Trend</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="tabs-list border-b border-[#E5E5E5] w-full justify-start bg-transparent p-0 h-auto">
          <TabsTrigger 
            value="overview" 
            className="tab-item data-[state=active]:text-[#2C4C3B] data-[state=active]:border-[#2C4C3B]"
            data-testid="tab-overview"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="interventions"
            className="tab-item data-[state=active]:text-[#2C4C3B] data-[state=active]:border-[#2C4C3B]"
            data-testid="tab-interventions"
          >
            Interventions ({interventions.length})
          </TabsTrigger>
          <TabsTrigger 
            value="progress"
            className="tab-item data-[state=active]:text-[#2C4C3B] data-[state=active]:border-[#2C4C3B]"
            data-testid="tab-progress"
          >
            Data ({progress.length})
          </TabsTrigger>
          <TabsTrigger 
            value="notes"
            className="tab-item data-[state=active]:text-[#2C4C3B] data-[state=active]:border-[#2C4C3B]"
            data-testid="tab-notes"
          >
            Notes ({notes.length})
          </TabsTrigger>
          <TabsTrigger 
            value="context"
            className="tab-item data-[state=active]:text-[#2C4C3B] data-[state=active]:border-[#2C4C3B]"
            data-testid="tab-context"
          >
            <UserCircle className="h-4 w-4 mr-1.5 inline-block" />
            Context Profile
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Student Summary Card */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Student Summary</h3>
              </div>
              <div className="card-content">
                {/* Quick Summary Alert for Review */}
                {student.review_status === 'overdue' && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-medium">Review date has passed ({student.review_date})</span>
                    </div>
                  </div>
                )}
                {student.review_status === 'review_soon' && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm font-medium">Review coming up on {student.review_date}</span>
                    </div>
                  </div>
                )}

                <div className="summary-box">
                  <div className="summary-row">
                    <span className="summary-label">Support Start Date</span>
                    <span className="summary-value font-medium">{student.support_start_date || '—'}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Days in Support</span>
                    <span className="summary-value">{daysInSupport !== null ? `${daysInSupport} days` : '—'}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Review Date</span>
                    <span className="summary-value font-medium">{student.review_date || '—'}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Active Interventions</span>
                    <span className="summary-value font-semibold text-[#2C4C3B]">{activeInterventions.length}</span>
                  </div>
                  {activeInterventions.length > 0 && (
                    <div className="summary-row">
                      <span className="summary-label">Intervention Focus</span>
                      <span className="summary-value text-[#1C1C1C]">
                        {[...new Set(activeInterventions.map(i => i.target_skill))].join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Active Interventions Quick View */}
                {activeInterventions.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
                    <h4 className="text-sm font-medium text-[#1C1C1C] mb-2">Active Interventions</h4>
                    <div className="space-y-2">
                      {activeInterventions.slice(0, 3).map(intervention => (
                        <div key={intervention.id} className="flex items-center justify-between p-2 bg-[#F3F4F1] rounded-lg">
                          <div>
                            <div className="text-sm font-medium">{intervention.intervention_name}</div>
                            <div className="text-xs text-[#595959]">{intervention.target_skill}</div>
                          </div>
                          <span className="text-xs text-[#595959]">{intervention.frequency_per_week}x/week</span>
                        </div>
                      ))}
                      {activeInterventions.length > 3 && (
                        <button 
                          onClick={() => setActiveTab('interventions')}
                          className="text-sm text-[#2C4C3B] hover:underline font-medium"
                        >
                          +{activeInterventions.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {student.notes && (
                  <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
                    <h4 className="text-sm font-medium text-[#1C1C1C] mb-2">Notes</h4>
                    <p className="text-sm text-[#595959] bg-[#F3F4F1] p-3 rounded-lg">{student.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Data Overview Card */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Data Overview</h3>
              </div>
              <div className="card-content">
                {/* High-level Data Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-[#F3F4F1] rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-[#2C4C3B]">{progress.length}</div>
                    <div className="text-xs text-[#595959]">Total Data Points</div>
                  </div>
                  <div className="bg-[#F3F4F1] rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-[#1C1C1C]">
                      {interventionsWithProgress.length}
                    </div>
                    <div className="text-xs text-[#595959]">Interventions with Data</div>
                  </div>
                </div>

                <div className="summary-box">
                  <div className="summary-row">
                    <span className="summary-label">Last Data Entry</span>
                    <span className="summary-value font-medium">{latestDate || '—'}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Latest Score</span>
                    <span className="summary-value font-semibold text-[#2C4C3B]">{latestScore || '—'}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Current Trend</span>
                    <span className={`summary-value trend-indicator ${getTrendClass(student.trend)}`}>
                      {getTrendIcon(student.trend)}
                      <span className="ml-1 capitalize">{student.trend || 'insufficient data'}</span>
                    </span>
                  </div>
                </div>

                {/* Recent Data by Intervention */}
                {interventionsWithProgress.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
                    <h4 className="text-sm font-medium text-[#1C1C1C] mb-2">Recent Data by Intervention</h4>
                    <div className="space-y-2">
                      {interventionsWithProgress.slice(0, 3).map(intervention => {
                        const interventionProgress = progress.filter(p => p.intervention_id === intervention.id);
                        const latestEntry = interventionProgress[0];
                        const dataCount = interventionProgress.length;
                        return (
                          <div key={intervention.id} className="p-3 bg-[#F3F4F1] rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-[#1C1C1C]">{intervention.intervention_name}</span>
                              <span className="text-xs text-[#595959]">{dataCount} data point{dataCount !== 1 ? 's' : ''}</span>
                            </div>
                            {latestEntry && (
                              <div className="flex items-center gap-4 text-xs text-[#595959]">
                                <span>Latest: <strong className="text-[#2C4C3B]">{latestEntry.score}</strong> {latestEntry.score_unit}</span>
                                <span>({latestEntry.date_collected})</span>
                              </div>
                            )}
                            {intervention.baseline_score && intervention.goal_score && (
                              <div className="text-xs text-[#595959] mt-1">
                                Baseline: {intervention.baseline_score} → Goal: {intervention.goal_score}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {interventionsWithProgress.length > 3 && (
                        <button 
                          onClick={() => setActiveTab('progress')}
                          className="text-sm text-[#2C4C3B] hover:underline font-medium"
                        >
                          View all on Data tab →
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {progress.length === 0 && (
                  <div className="mt-4 p-4 bg-[#F3F4F1] rounded-lg text-center">
                    <TrendingUp className="h-8 w-8 text-[#A3A3A3] mx-auto mb-2" />
                    <p className="text-sm text-[#595959]">No data yet</p>
                    <p className="text-xs text-[#A3A3A3] mt-1">Add data from the Data tab</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI Summary Card */}
          <div className="card mt-6">
            <div className="card-header flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#2C4C3B]" />
                <h3 className="card-title">Draft Summary with AI</h3>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleGenerateAISummary}
                disabled={aiSummaryLoading}
                className="border-[#2C4C3B]/30 text-[#2C4C3B] hover:bg-[#2C4C3B]/10"
                data-testid="generate-ai-summary-btn"
              >
                {aiSummaryLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Draft
                  </>
                )}
              </Button>
            </div>
            <div className="card-content">
              {!aiSummary && !aiSummaryLoading && (
                <div className="p-6 bg-[#F3F4F1] rounded-lg text-center">
                  <Sparkles className="h-8 w-8 text-[#A3A3A3] mx-auto mb-2" />
                  <p className="text-sm text-[#595959]">Generate an AI-drafted summary</p>
                  <p className="text-xs text-[#A3A3A3] mt-1">Uses student data, interventions, and progress to create a narrative summary</p>
                </div>
              )}
              
              {aiSummaryLoading && (
                <div className="p-6 bg-[#2C4C3B]/10 rounded-lg text-center">
                  <Loader2 className="h-8 w-8 text-[#2C4C3B]/70 mx-auto mb-2 animate-spin" />
                  <p className="text-sm text-[#2C4C3B]">Generating summary...</p>
                  <p className="text-xs text-[#2C4C3B]/70 mt-1">This may take a few seconds</p>
                </div>
              )}
              
              {aiSummary && !aiSummaryLoading && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#595959]">AI-generated draft • Review and edit as needed</span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSaveAISummaryToNotes}
                        disabled={savingAISummaryToNotes || !aiSummary.trim()}
                        className="border-[#2C4C3B]/30 text-[#2C4C3B] hover:bg-[#2C4C3B]/10"
                        data-testid="save-ai-to-notes-btn"
                      >
                        {savingAISummaryToNotes ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-1" />
                            Save to Notes
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCopyAISummary}
                        className="text-[#595959] hover:text-[#1C1C1C]"
                      >
                        {aiSummaryCopied ? (
                          <>
                            <Check className="h-4 w-4 mr-1 text-green-500" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setAiSummary('')}
                        className="text-[#595959] hover:text-red-600"
                        data-testid="clear-ai-summary-btn"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear
                      </Button>
                    </div>
                  </div>
                  <textarea
                    value={aiSummary}
                    onChange={(e) => setAiSummary(e.target.value)}
                    className="w-full min-h-[200px] p-4 border border-[#E5E5E5] rounded-lg text-sm text-[#1C1C1C] leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
                    placeholder="AI summary will appear here..."
                    data-testid="ai-summary-textarea"
                  />
                  <p className="text-xs text-[#A3A3A3]">
                    This is a draft for your review. Edit as needed. Use "Save to Notes" to add it to this student's notes, or "Copy" to use elsewhere.
                  </p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Interventions Tab */}
        <TabsContent value="interventions" className="mt-6">
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <h3 className="card-title">Interventions</h3>
              <Button 
                size="sm" 
                className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium"
                onClick={() => setShowAddIntervention(true)}
                data-testid="add-intervention-tab-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Intervention
              </Button>
            </div>
            <div className="card-content p-0">
              {interventions.length === 0 ? (
                <div className="empty-state py-12">
                  <div className="empty-icon">
                    <Target size={32} />
                  </div>
                  <h3 className="empty-title">No interventions yet</h3>
                  <p className="empty-text">Add an intervention to start tracking support</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="interventions-table">
                    <thead>
                      <tr>
                        <th>Intervention</th>
                        <th>Target Skill</th>
                        <th>Provider</th>
                        <th>Frequency</th>
                        <th>Status</th>
                        <th>Start Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {interventions.map((intervention) => (
                        <tr 
                          key={intervention.id} 
                          data-testid={`intervention-row-${intervention.id}`}
                          className="group hover:bg-[#2C4C3B]/5 transition-colors"
                        >
                          <td 
                            className="font-medium cursor-pointer hover:text-[#2C4C3B]"
                            onClick={() => handleNavigateToInterventionProgress(intervention.id)}
                          >
                            <div className="flex items-center gap-2">
                              {intervention.intervention_name}
                              <span className="text-xs text-[#A3A3A3] opacity-0 group-hover:opacity-100 transition-opacity">
                                → View Data
                              </span>
                            </div>
                          </td>
                          <td 
                            className="cursor-pointer"
                            onClick={() => handleNavigateToInterventionProgress(intervention.id)}
                          >{intervention.target_skill}</td>
                          <td 
                            className="cursor-pointer"
                            onClick={() => handleNavigateToInterventionProgress(intervention.id)}
                          >{intervention.provider_name}</td>
                          <td 
                            className="cursor-pointer"
                            onClick={() => handleNavigateToInterventionProgress(intervention.id)}
                          >{intervention.frequency_per_week}x/week, {intervention.session_minutes}min</td>
                          <td 
                            className="cursor-pointer"
                            onClick={() => handleNavigateToInterventionProgress(intervention.id)}
                          >
                            <span className={`badge ${getInterventionStatusClass(intervention.active_status)}`}>
                              {intervention.active_status}
                            </span>
                          </td>
                          <td 
                            className="cursor-pointer"
                            onClick={() => handleNavigateToInterventionProgress(intervention.id)}
                          >{intervention.start_date}</td>
                          <td>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditIntervention(intervention);
                                }}
                                title="Edit Intervention"
                                data-testid={`edit-intervention-${intervention.id}`}
                              >
                                <Edit className="h-4 w-4 text-[#595959]" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddProgressFromIntervention(intervention.id);
                                }}
                                title="Add Data"
                                data-testid={`add-progress-intervention-${intervention.id}`}
                                className="text-[#2C4C3B] border-[#2C4C3B]/30 hover:bg-[#2C4C3B]/10"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Data
                              </Button>
                              {intervention.active_status === 'Active' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkCompleted(intervention.id);
                                  }}
                                  title="Mark Completed"
                                  data-testid={`complete-intervention-${intervention.id}`}
                                >
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                </Button>
                              )}
                              {/* Separator before destructive action */}
                              <div className="w-px h-5 bg-slate-200 mx-1"></div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteIntervention(intervention.id);
                                }}
                                title="Delete"
                                data-testid={`delete-intervention-${intervention.id}`}
                                className="hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Data Tab */}
        <TabsContent value="progress" className="mt-6">
          {/* Graph Mode Toggle */}
          <div className="mb-4 flex items-center gap-2" data-testid="graph-mode-toggle">
            <span className="text-sm font-medium text-[#1C1C1C]">Graph Mode:</span>
            <div className="inline-flex rounded-lg border border-[#E5E5E5] p-1 bg-[#F3F4F1]">
              <button
                onClick={() => handleGraphModeChange('single')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  graphMode === 'single'
                    ? 'bg-white text-[#2C4C3B] shadow-sm'
                    : 'text-[#595959] hover:text-slate-900'
                }`}
                data-testid="graph-mode-single"
              >
                Single Intervention
              </button>
              <button
                onClick={() => handleGraphModeChange('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  graphMode === 'all'
                    ? 'bg-white text-[#2C4C3B] shadow-sm'
                    : 'text-[#595959] hover:text-slate-900'
                }`}
                data-testid="graph-mode-all"
              >
                All Interventions
              </button>
            </div>
          </div>

          {/* Single Intervention Mode */}
          {graphMode === 'single' && (
            <>
              {/* Intervention Filter */}
              <div className="mb-6 flex items-center gap-4" data-testid="progress-intervention-filter">
                <label className="text-sm font-medium text-[#1C1C1C]">Select Intervention:</label>
                <select
                  value={selectedInterventionFilter}
                  onChange={(e) => setSelectedInterventionFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[200px]"
                  data-testid="intervention-filter-select"
                >
                  <option value="all">— Select an intervention —</option>
                  {interventions.map((intervention) => (
                    <option key={intervention.id} value={intervention.id}>
                      {intervention.intervention_name}
                    </option>
                  ))}
                </select>
                {selectedInterventionFilter !== 'all' && selectedIntervention && (
                  <div className="flex items-center gap-4 text-sm text-[#595959] ml-4">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-0.5 bg-amber-500 inline-block"></span>
                      Baseline: <strong>{selectedIntervention.baseline_score ?? '—'}</strong>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-0.5 bg-emerald-500 inline-block"></span>
                      Goal: <strong>{selectedIntervention.goal_score ?? '—'}</strong>
                    </span>
                  </div>
                )}
              </div>

              {/* Progress Chart - Single Mode */}
              {selectedInterventionFilter === 'all' ? (
                <div className="chart-container mb-6 bg-[#F3F4F1] border-2 border-dashed border-[#E5E5E5]" data-testid="progress-chart-placeholder">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Activity className="h-12 w-12 text-[#A3A3A3] mb-3" />
                    <h3 className="text-lg font-medium text-[#1C1C1C] mb-1">Select an Intervention to View Graph</h3>
                    <p className="text-sm text-[#595959] max-w-md">
                      Choose a specific intervention from the dropdown above to see its progress graph with baseline and goal reference lines.
                    </p>
                  </div>
                </div>
              ) : chartData.length > 1 ? (
                <div className="chart-container mb-6" data-testid="progress-chart">
                  <div className="chart-header flex items-center justify-between">
                    <h3 className="chart-title">
                      Data Over Time: {selectedIntervention?.intervention_name}
                    </h3>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-[#595959]">
                        {chartData.length} data point{chartData.length !== 1 ? 's' : ''}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPrintModal(true)}
                        className="text-[#595959] hover:text-[#2C4C3B]"
                        data-testid="print-graph-btn"
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print
                      </Button>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#4f46e5" 
                        strokeWidth={2}
                        dot={{ fill: '#4f46e5', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                      {selectedIntervention?.goal_score && (
                        <ReferenceLine 
                          y={selectedIntervention.goal_score} 
                          stroke="#10b981" 
                          strokeDasharray="5 5"
                          label={{ value: 'Goal', fill: '#10b981', fontSize: 12 }}
                        />
                      )}
                      {selectedIntervention?.baseline_score && (
                        <ReferenceLine 
                          y={selectedIntervention.baseline_score} 
                          stroke="#f59e0b" 
                          strokeDasharray="5 5"
                          label={{ value: 'Baseline', fill: '#f59e0b', fontSize: 12 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="chart-container mb-6 bg-[#F3F4F1] border-2 border-dashed border-[#E5E5E5]" data-testid="progress-chart-no-data">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <TrendingUp className="h-12 w-12 text-[#A3A3A3] mb-3" />
                    <h3 className="text-lg font-medium text-[#1C1C1C] mb-1">Not Enough Data for Graph</h3>
                    <p className="text-sm text-[#595959] max-w-md">
                      Add at least 2 data points for "{selectedIntervention?.intervention_name}" to see the graph.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* All Interventions Mode */}
          {graphMode === 'all' && (
            <>
              {/* Unit mismatch warning */}
              {hasMultipleUnits && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="unit-mismatch-warning">
                  <div className="flex items-start gap-2 text-amber-700">
                    <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Different Score Units Detected</p>
                      <p className="text-xs mt-0.5">
                        Interventions use different units ({uniqueUnits.join(', ')}). Direct comparison may not be meaningful.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Data Chart - All Interventions Mode */}
              {interventionsWithProgress.length === 0 ? (
                <div className="chart-container mb-6 bg-[#F3F4F1] border-2 border-dashed border-[#E5E5E5]" data-testid="progress-chart-no-interventions">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Activity className="h-12 w-12 text-[#A3A3A3] mb-3" />
                    <h3 className="text-lg font-medium text-[#1C1C1C] mb-1">No Intervention Data to Compare</h3>
                    <p className="text-sm text-[#595959] max-w-md">
                      Add data to interventions to see them compared on this graph.
                    </p>
                  </div>
                </div>
              ) : multiLineChartData.length > 1 ? (
                <div className="chart-container mb-6" data-testid="progress-chart-all">
                  <div className="chart-header flex items-center justify-between">
                    <h3 className="chart-title">
                      Data Comparison: All Interventions
                    </h3>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-[#595959]">
                        {interventionsWithProgress.length} intervention{interventionsWithProgress.length !== 1 ? 's' : ''}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPrintModal(true)}
                        className="text-[#595959] hover:text-[#2C4C3B]"
                        data-testid="print-graph-all-btn"
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print
                      </Button>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={multiLineChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value, name) => {
                          const intervention = interventionsWithProgress.find(i => i.id === name);
                          return [value, intervention?.intervention_name || name];
                        }}
                      />
                      <Legend 
                        formatter={(value) => {
                          const intervention = interventionsWithProgress.find(i => i.id === value);
                          return intervention?.intervention_name || value;
                        }}
                        wrapperStyle={{ paddingTop: '10px' }}
                      />
                      {interventionsWithProgress.map((intervention, index) => (
                        <Line
                          key={intervention.id}
                          type="monotone"
                          dataKey={intervention.id}
                          name={intervention.id}
                          stroke={lineColors[index % lineColors.length]}
                          strokeWidth={2}
                          dot={{ fill: lineColors[index % lineColors.length], r: 4 }}
                          activeDot={{ r: 6 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="chart-container mb-6 bg-[#F3F4F1] border-2 border-dashed border-[#E5E5E5]" data-testid="progress-chart-insufficient">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <TrendingUp className="h-12 w-12 text-[#A3A3A3] mb-3" />
                    <h3 className="text-lg font-medium text-[#1C1C1C] mb-1">Not Enough Data for Comparison</h3>
                    <p className="text-sm text-[#595959] max-w-md">
                      Add progress data on at least 2 different dates to see the comparison graph.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Progress Data Table */}
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <h3 className="card-title">
                Progress Data
                {graphMode === 'single' && selectedInterventionFilter !== 'all' && (
                  <span className="text-sm font-normal text-[#595959] ml-2">
                    ({filteredProgressForTable.length} of {progress.length} entries)
                  </span>
                )}
              </h3>
              <Button 
                size="sm" 
                className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium"
                onClick={handleAddProgressGeneral}
                data-testid="add-progress-tab-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Data Point
              </Button>
            </div>
            <div className="card-content p-0">
              {filteredProgressForTable.length === 0 ? (
                <div className="empty-state py-12">
                  <div className="empty-icon">
                    <TrendingUp size={32} />
                  </div>
                  <h3 className="empty-title">
                    {selectedInterventionFilter !== 'all' 
                      ? `No data for this intervention` 
                      : 'No data yet'}
                  </h3>
                  <p className="empty-text">
                    {selectedInterventionFilter !== 'all'
                      ? 'Add data to start tracking this intervention'
                      : 'Add data to track student growth'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="progress-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Intervention</th>
                        <th>Measure</th>
                        <th>Score</th>
                        <th>Unit</th>
                        <th>Note</th>
                        <th>Entered By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProgressForTable.map((p) => (
                        <tr key={p.id} data-testid={`progress-row-${p.id}`}>
                          <td>{p.date_collected}</td>
                          <td>
                            {p.intervention_name ? (
                              <span className="text-sm font-medium text-[#1C1C1C]">{p.intervention_name}</span>
                            ) : (
                              <span className="text-sm text-[#A3A3A3]">—</span>
                            )}
                          </td>
                          <td>{p.measure_name}</td>
                          <td className="font-semibold">{p.score}</td>
                          <td>{p.score_unit}</td>
                          <td>{p.note || '—'}</td>
                          <td>
                            {p.entered_by_name ? (
                              <div>
                                <span className="text-sm">{p.entered_by_name}</span>
                                {p.entered_by_email && (
                                  <span className="block text-xs text-[#A3A3A3]">{p.entered_by_email}</span>
                                )}
                              </div>
                            ) : p.entered_by ? (
                              <span className="text-sm">{p.entered_by}</span>
                            ) : (
                              <span className="text-sm text-[#A3A3A3]">—</span>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProgress(p.id)}
                              title="Delete"
                              data-testid={`delete-progress-${p.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-6" data-testid="notes-tab-content">
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <h3 className="card-title">
                <StickyNote className="h-5 w-5 inline mr-2 text-[#2C4C3B]" />
                Notes
              </h3>
              <Button 
                size="sm" 
                className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium"
                onClick={() => setShowAddNote(true)}
                data-testid="add-note-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </div>
            <div className="card-content">
              {notes.length === 0 ? (
                <div className="text-center py-12 text-[#A3A3A3]">
                  <StickyNote className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-base">No notes yet</p>
                  <p className="text-sm mt-1">Click "Add Note" to create the first note for this student.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div 
                      key={note.id} 
                      className="p-4 border border-[#E5E5E5] rounded-xl hover:border-[#2C4C3B]/30 transition-colors"
                      data-testid={`note-${note.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-[#2C4C3B]">
                            {new Date(note.note_date + 'T00:00:00').toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                          {note.entered_by_name && (
                            <span className="text-xs text-[#A3A3A3]">
                              by {note.entered_by_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditNote(note)}
                            className="h-8 w-8 p-0 text-[#595959] hover:text-[#2C4C3B]"
                            title="Edit note"
                            data-testid={`edit-note-${note.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNote(note.id)}
                            className="h-8 w-8 p-0 text-[#595959] hover:text-red-600"
                            title="Delete note"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-[#1C1C1C] whitespace-pre-wrap leading-relaxed">
                        {note.note_text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Context Profile Tab */}
        <TabsContent value="context" className="mt-6" data-testid="context-tab-content">
          <StudentContextProfile 
            studentId={id} 
            canEdit={canEdit}
          />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {showEditStudent && (
        <EditStudentModal
          student={student}
          onClose={() => setShowEditStudent(false)}
          onSave={() => {
            setShowEditStudent(false);
            fetchStudentData();
          }}
        />
      )}

      {showAddIntervention && (
        <AddInterventionModal
          studentId={id}
          student={student}
          onClose={() => setShowAddIntervention(false)}
          onSave={() => {
            setShowAddIntervention(false);
            fetchStudentData();
          }}
        />
      )}

      {showAddProgress && (
        <AddProgressModal
          studentId={id}
          studentName={student.full_name}
          measureName={student.baseline_measure_name}
          interventions={interventions}
          selectedInterventionId={selectedInterventionForProgress}
          onClose={() => {
            setShowAddProgress(false);
            setSelectedInterventionForProgress(null);
          }}
          onSave={() => {
            setShowAddProgress(false);
            setSelectedInterventionForProgress(null);
            fetchStudentData();
          }}
        />
      )}

      {showEditIntervention && selectedInterventionForEdit && (
        <EditInterventionModal
          intervention={selectedInterventionForEdit}
          student={student}
          onClose={() => {
            setShowEditIntervention(false);
            setSelectedInterventionForEdit(null);
          }}
          onSave={() => {
            setShowEditIntervention(false);
            setSelectedInterventionForEdit(null);
            fetchStudentData();
          }}
        />
      )}

      {/* Print Graph Modal */}
      <PrintGraphModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        studentName={student?.name}
        interventionName={graphMode === 'single' && selectedIntervention ? selectedIntervention.intervention_name : null}
        chartData={graphMode === 'single' ? chartData : multiLineChartData}
        tableData={graphMode === 'single' 
          ? filteredProgressForChart.map(p => ({
              date_collected: p.date_collected,
              score: p.score,
              score_unit: p.score_unit,
              intervention_name: interventions.find(i => i.id === p.intervention_id)?.intervention_name,
              note: p.note
            }))
          : progress.filter(p => interventionsWithProgress.some(i => i.id === p.intervention_id)).map(p => ({
              date_collected: p.date_collected,
              score: p.score,
              score_unit: p.score_unit,
              intervention_name: interventions.find(i => i.id === p.intervention_id)?.intervention_name,
              note: p.note
            }))
        }
        chartType={graphMode === 'single' ? 'single' : 'multi'}
        interventions={interventionsWithProgress}
        baseline={selectedIntervention?.baseline_score}
        goal={selectedIntervention?.goal_score}
        scoreUnit={selectedIntervention?.score_unit || '%'}
      />

      {/* Add Note Modal */}
      {showAddNote && (
        <AddNoteModal
          studentId={id}
          studentName={student.full_name}
          onClose={() => setShowAddNote(false)}
          onSave={() => {
            setShowAddNote(false);
            fetchStudentData();
          }}
        />
      )}

      {/* Edit Note Modal */}
      {showEditNote && selectedNoteForEdit && (
        <EditNoteModal
          note={selectedNoteForEdit}
          studentName={student.full_name}
          onClose={() => {
            setShowEditNote(false);
            setSelectedNoteForEdit(null);
          }}
          onSave={() => {
            setShowEditNote(false);
            setSelectedNoteForEdit(null);
            fetchStudentData();
          }}
        />
      )}
    </div>
  );
}
